import { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import Modal from "@shared/v2/Modal";
import Button from "@shared/v2/Button";
import Alert from "@shared/v2/Alert";
import ChooseContact from "./ChooseContact";
import MergeContacts from "./MergeContacts";
import {
  useContactMergeLoading,
  useContactMergeModalIsOpen,
  useContactMergeOtherContact,
  useContactMergePrimaryContact,
  useContactMergeError,
  useContactMergePrimaryContactSlug,
  useContactMergeOtherContactSlug,
  useContactMergePage,
} from "../reducers/contactMergeReducer/selectors";
import * as actions from "../reducers/contactMergeReducer";
import Loading from "../Goals/components/Loader";

const getTitle = (page) => {
  if (page === 2) return "Merge Lead";
  return "Select a Lead to Merge";
};

const ContactMergeModal = ({ push }) => {
  const dispatch = useDispatch();
  const person = useSelector((state) => state.personDetail.person);
  const modalIsOpen = useContactMergeModalIsOpen();
  const primaryContact = useContactMergePrimaryContact();
  const otherContact = useContactMergeOtherContact();
  const primaryContactSlug = useContactMergePrimaryContactSlug();
  const otherContactSlug = useContactMergeOtherContactSlug();
  const loading = useContactMergeLoading();
  const error = useContactMergeError();
  const page = useContactMergePage();

  useEffect(() => {
    if (modalIsOpen) {
      if (primaryContactSlug) {
        if (otherContactSlug) dispatch(actions.getContactsFromSlugs());
        else dispatch(actions.getPotentialDuplicates({ slug: primaryContactSlug }));
      } else if (!primaryContact && person.data) {
        dispatch(actions.getPotentialDuplicates({ slug: person.data.attributes.slug }));
      }
    }
    if (!modalIsOpen) dispatch(actions.setPage(1));
  }, [primaryContactSlug, otherContactSlug, primaryContact, modalIsOpen]);

  const onClose = () => dispatch(actions.setModalIsOpen(false));

  return (
    <Modal
      closeOnEscape={false}
      closeOnClickOutside={false}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[700px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col"
      show={modalIsOpen}
      onHide={onClose}
    >
      <Modal.Header title={getTitle(page)} closeButton={!loading} />
      <Modal.Body className="tw-relative tw-flex tw-flex-col tw-overflow-hidden tw-mt-[12px] tw-mb-[32px]">
        {loading && <Loading />}
        {primaryContact && page === 1 && <ChooseContact />}
        {primaryContact && otherContact && page === 2 && <MergeContacts />}
      </Modal.Body>
      <Modal.Footer className="tw-flex tw-flex-col tw-gap-[24px]">
        {error && <Alert variant="error" text={error} />}
        <div className="tw-flex tw-justify-between tw-gap-[8px]">
          <Button
            size="medium"
            schema="tertiary"
            onClick={page <= 1 ? onClose : () => dispatch(actions.setPage(1))}
            disabled={loading}
          >
            {page > 1 ? "Back" : "Cancel"}
          </Button>
          <Button
            size="medium"
            schema="primary"
            onClick={
              page <= 1
                ? () => {
                    dispatch(
                      actions.setKeepContact(otherContact.hasListings ? otherContact : primaryContact),
                    );
                    dispatch(actions.setPage(2));
                  }
                : () =>
                    dispatch(actions.mergeContacts()).then(({ meta, payload: mergeContact }) => {
                      if (meta.requestStatus === "fulfilled") {
                        onClose();
                        if (primaryContact.slug !== mergeContact.slug && push) {
                          push(`/people/${mergeContact.slug}`);
                        } else {
                          window.location.reload();
                        }
                      }
                    })
            }
            disabled={page === 1 ? !otherContact : false}
            isLoading={loading}
          >
            {page === 2 ? "Merge" : "Next"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

ContactMergeModal.propTypes = {
  push: PropTypes.func,
};

ContactMergeModal.defaultProps = {
  push: undefined,
};

export default ContactMergeModal;
